import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { EventOut, ProductOut } from '@/types/api';
import Button from '@/components/atoms/Button';
import { SearchableSelect } from '@/components/molecules/SearchableSelect';
import { productsUrl } from '@/utils/productLinks';
import './CartaProductPicker.scss';

interface CartaProductPickerProps {
  /** Event the carta row belongs to (undefined until one is picked). */
  event?: EventOut;
  /** Catalog products of the event's Local, already filtered by kind. */
  products: ProductOut[];
  /** False while the Local's products are still loading. */
  productsLoaded: boolean;
  value: number | null;
  onChange: (productId: number | null) => void;
  kind: 'drink' | 'food';
  /** Name of the product's type, for the read-only preview. */
  typeName?: string;
  error?: string;
  disabled?: boolean;
}

/**
 * First field of the carta form: the catalog Product this listing sells.
 * Every carta row comes from a Product of the event's Local, so this also
 * explains what to do when the event has no Local or the Local has no
 * products yet, and shows the chosen product's catalog details read-only.
 */
const CartaProductPicker: React.FC<CartaProductPickerProps> = ({
  event,
  products,
  productsLoaded,
  value,
  onChange,
  kind,
  typeName,
  error,
  disabled,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const selected = products.find((p) => p.id === value);
  const options = useMemo(
    () => products.map((product) => ({ value: product.id, label: product.name })),
    [products]
  );

  if (!event) {
    return (
      <div className="carta-product-picker carta-product-picker--hint form-field--full-width">
        <p>{t('carta.productPicker.selectEventFirst')}</p>
      </div>
    );
  }

  if (!event.local_id) {
    return (
      <div className="carta-product-picker carta-product-picker--warning form-field--full-width">
        <strong>{t('carta.productPicker.noLocalTitle')}</strong>
        <p>{t('carta.productPicker.noLocalText')}</p>
        <Button type="button" variant="outline" size="sm" onClick={() => navigate('/events/manage')}>
          {t('carta.productPicker.goToEvents')}
        </Button>
      </div>
    );
  }

  if (productsLoaded && products.length === 0) {
    return (
      <div className="carta-product-picker carta-product-picker--empty form-field--full-width">
        <strong>{t('carta.productPicker.emptyTitle')}</strong>
        <p>{t(kind === 'drink' ? 'carta.productPicker.emptyTextDrinks' : 'carta.productPicker.emptyTextFoods')}</p>
        <Button
          type="button"
          variant="primary"
          size="sm"
          onClick={() => navigate(productsUrl(event.local_id, kind))}
        >
          {t('carta.productPicker.goToProducts')}
        </Button>
      </div>
    );
  }

  return (
    <div className="carta-product-picker form-field--full-width">
      <SearchableSelect<number>
        name="product_id"
        label={t('carta.productPicker.label')}
        helperText={t('carta.productPicker.helper')}
        placeholder={t('carta.productPicker.placeholder')}
        error={error}
        required
        options={options}
        value={value}
        onChange={onChange}
        disabled={disabled || !productsLoaded}
        isLoading={!productsLoaded}
      />

      <div className="carta-product-picker__create">
        <span>{t('carta.productPicker.notInCatalog')}</span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => navigate(productsUrl(event.local_id, kind))}
        >
          {t('carta.createProduct')}
        </Button>
      </div>

      {selected && (
        <div className="carta-product-picker__preview">
          {selected.image_url && (
            <img src={selected.image_url} alt={selected.name} className="carta-product-picker__preview-image" />
          )}
          <div className="carta-product-picker__preview-body">
            <strong>{selected.name}</strong>
            {selected.description && <p>{selected.description}</p>}
            <span className="carta-product-picker__preview-meta">
              {typeName ? `${typeName} · ` : ''}
              {t('carta.productPicker.stock', { count: selected.stock?.quantity ?? 0 })}
            </span>
          </div>
          <p className="carta-product-picker__preview-note">
            {t('carta.productPicker.managedInProducts')}
          </p>
        </div>
      )}
    </div>
  );
};

export default CartaProductPicker;
